import { useCallback, useEffect, useRef } from "react";
import { useChatStore } from "@/stores/useChatStore";
import { formatBytes } from "@/lib/format-bytes";
import { editInputDiff } from "@/lib/edit-input-diff";
import { DiffLineStats } from "@/components/diff/DiffLineStats";
import { CODE_BOX_CLASS } from "@/components/chat/ToolEntry";
import { DiffFileLink } from "@/components/diff/DiffFileLink";
import { DiffHunkList } from "@/components/diff/DiffHunkList";
import { formatFilePolicyEntry, formatToolPolicyEntry } from "@/lib/tool-policy-document";
import { editorCommandLabel, toolDisplayName } from "@/lib/tool-catalog";
import type { ToolApprovalMarker } from "@/lib/tool-approval";
import { ChoiceCard } from "@/components/hitl/choice-card/ChoiceCard";
import { useChoiceOptionFocus } from "@/components/hitl/choice-card/useChoiceOptionFocus";
import type { ToolApprovalDecision } from "@/lib/api";
import { ToolApprovalOptionButton } from "./ToolApprovalOptionButton";
import { useResolveToolApproval } from "./useResolveToolApproval";

/** サーバの ToolPolicyCategory を日本語ラベルへ写す。 */
const CATEGORY_LABELS: Record<string, string> = {
	Bash: "シェル実行",
	EditorRead: "ファイル読み取り",
	EditorWrite: "ファイル編集",
	Subagent: "サブエージェント",
	InternalDataPlane: "内部データ処理",
	ExternalRead: "外部情報の取得",
	DatasetRead: "データセット読み取り",
	SandboxWrite: "サンドボックス書き込み",
	ModelTraining: "学習ジョブ",
	ModelTrainingRead: "学習ジョブの参照",
	Monitor: "モニター",
	Browser: "ブラウザ操作",
	KnowledgeGraph: "ナレッジグラフの編集",
	KnowledgeGraphRead: "ナレッジグラフの参照",
	Unknown: "未分類",
};

/**
 * 確認になった原因のポリシールールを、ポリシーの JSON と同じ書き方で返す。
 * 確認モード (手動確認) による確認はルールに一致していないので null。
 */
function approvalReason(marker: ToolApprovalMarker): string | null {
	if (marker.matchedRule) return formatToolPolicyEntry(marker.matchedRule);
	if (marker.matchedFileRule) return formatFilePolicyEntry(marker.matchedFileRule);
	return null;
}

const DELEGATION_NOTE =
	"配下のサブエージェントは、シェル実行・ファイル編集を確認なしで行います（ツール実行ポリシーで拒否・確認に指定した操作は行いません。ただし、シェルのコマンドから読み取れない操作は止められないことがあります）";

function text(value: unknown): string | null {
	return typeof value === "string" && value.trim() !== "" ? value : null;
}

function Detail({ label, value }: { label: string; value: string | null }) {
	if (value === null) return null;
	return (
		// 見出しの列は、出てきうる見出しでいちばん長い「作業ディレクトリ」(全角 8 文字) に固定してそろえる。
		<div className="grid grid-cols-[8em_minmax(0,1fr)] gap-3">
			<span className="text-fg-2">{label}</span>
			<code className="whitespace-pre-wrap text-fg-1 [overflow-wrap:anywhere]">{value}</code>
		</div>
	);
}

/**
 * チャットの Markdown のコードブロックに近い見た目で、見出しを付けずにコマンドを出す。
 * 承認前に末尾まで読めるよう、折り返して高さも制限しない。
 */
function CommandBlock({ command }: { command: string | null }) {
	if (command === null) return null;
	return <pre className={`${CODE_BOX_CLASS} whitespace-pre-wrap wrap-anywhere`}>{command}</pre>;
}

/** ファイル編集は、チャットのファイル編集と同じ見た目でファイル名と差分を出す。 */
function EditDetails({ input }: { input: Record<string, unknown> }) {
	const path = text(input.path);
	const diff = editInputDiff(input);
	return (
		<>
			{path && (
				<div className="flex min-w-0 items-start gap-1 font-mono text-fg-3">
					{/* 作成はまだファイルがなく開けないので、名前だけ出す。 */}
					{input.command === "create" ? (
						<span className="min-w-0 break-all text-fg-2">{path}</span>
					) : (
						<DiffFileLink path={path} wrap />
					)}
					{diff && <DiffLineStats added={diff.added} removed={diff.removed} />}
				</div>
			)}
			{diff && (
				<DiffHunkList
					hunks={diff.hunks}
					truncated={diff.truncated}
					lineNumbers={diff.lineNumbers}
					wrap
				/>
			)}
		</>
	);
}

/**
 * DAG 1 ノードの表の 1 行。承認すると配下は確認なしで動くため、その動きを決める prompt も読めるようにする。
 * 長くなりうるので畳んでおく。
 */
function WorkflowNodeRow({ node }: { node: unknown }) {
	const { id, profile, description, prompt, needs } = (node ?? {}) as {
		id?: unknown;
		profile?: unknown;
		description?: unknown;
		prompt?: unknown;
		needs?: unknown;
	};
	const instruction = text(prompt);
	const upstream = Array.isArray(needs)
		? needs.map((need) => text(need)).filter((need) => need !== null)
		: [];
	return (
		<tr className="align-top">
			<td className="whitespace-nowrap px-2 py-1 font-mono text-fg-1">{text(id) ?? "(id なし)"}</td>
			<td className="whitespace-nowrap px-2 py-1 text-fg-2">{text(profile) ?? "—"}</td>
			<td className="px-2 py-1 text-fg-1 [overflow-wrap:anywhere]">
				{text(description) ?? "—"}
				{instruction && (
					<details className="mt-0.5">
						<summary className="cursor-pointer text-fg-3">指示</summary>
						<p className="m-0 mt-0.5 whitespace-pre-wrap text-fg-2">{instruction}</p>
					</details>
				)}
			</td>
			<td className="px-2 py-1 font-mono text-fg-3 [overflow-wrap:anywhere]">
				{upstream.length > 0 ? upstream.join(", ") : "—"}
			</td>
		</tr>
	);
}

/**
 * DAG は最大 64 ノード × 40,000 文字の prompt になりうる。生 JSON をそのまま出すと承認カードが
 * 読めない量になるため、承認の判断に要る形 (どの profile が何件、依存はどうか) の表へ畳む。
 */
function WorkflowDetails({ definition }: { definition: unknown }) {
	if (typeof definition !== "object" || definition === null) {
		return <Detail label="入力" value={JSON.stringify(definition, null, 2)} />;
	}
	const { description, phases, nodes } = definition as {
		description?: unknown;
		phases?: unknown;
		nodes?: unknown;
	};
	const summary = text(description);
	const nodeList = Array.isArray(nodes) ? nodes : [];
	const phaseTitles = Array.isArray(phases)
		? phases.map((phase) => text((phase as { title?: unknown })?.title)).filter((t) => t !== null)
		: [];
	return (
		<>
			{summary && <p className="m-0 text-fg-1 [overflow-wrap:anywhere]">{summary}</p>}
			{phaseTitles.length > 0 && (
				<div className="flex flex-wrap items-center gap-1.5 text-fg-3">
					{phaseTitles.map((title, index) => (
						<span key={index} className="flex items-center gap-1.5">
							{index > 0 && "→"}
							<span className="rounded-2 bg-surface-2 px-1.5 text-fg-1">{title}</span>
						</span>
					))}
				</div>
			)}
			<div className="overflow-x-auto rounded-2 shadow-[inset_0_0_0_1px_var(--line-1)]">
				<table className="w-full border-collapse text-left">
					<thead className="bg-surface-2 text-fg-3">
						<tr>
							<th className="whitespace-nowrap px-2 py-1 font-medium">
								ノード {nodeList.length}件
							</th>
							<th className="whitespace-nowrap px-2 py-1 font-medium">プロファイル</th>
							<th className="whitespace-nowrap px-2 py-1 font-medium">内容</th>
							<th className="whitespace-nowrap px-2 py-1 font-medium">依存</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-line-1">
						{nodeList.map((node, index) => (
							<WorkflowNodeRow key={index} node={node} />
						))}
					</tbody>
				</table>
			</div>
		</>
	);
}

function ToolDetails({
	name,
	input,
	workingDirectory,
}: {
	name: string;
	input: Record<string, unknown>;
	workingDirectory?: string;
}) {
	if (name === "bash") {
		return (
			<>
				<CommandBlock command={text(input.command)} />
				<Detail label="作業ディレクトリ" value={text(input.cwd) ?? workingDirectory ?? null} />
			</>
		);
	}
	if (name === "str_replace_based_edit_tool") {
		return <EditDetails input={input} />;
	}
	if (name === "run_subagent") {
		return (
			<>
				<Detail label="プロファイル" value={text(input.profile)} />
				<Detail label="概要" value={text(input.description)} />
				<Detail label="指示" value={text(input.prompt)} />
				<Detail
					label="実行"
					value={
						input.run_in_background === false && text(input.phase) === null
							? "フォアグラウンド"
							: "バックグラウンド"
					}
				/>
				<Detail label="承認すると" value={DELEGATION_NOTE} />
			</>
		);
	}
	if (name === "run_workflow") {
		return (
			<>
				<WorkflowDetails definition={input.definition} />
				<Detail label="承認すると" value={DELEGATION_NOTE} />
			</>
		);
	}
	if (name === "submit_model_training") {
		return (
			<>
				<Detail label="GPU" value={text(input.gpu)} />
				<Detail
					label="Timeout"
					value={typeof input.timeout_seconds === "number" ? `${input.timeout_seconds}秒` : null}
				/>
				<Detail label="Dataset" value={text(input.dataset_path)} />
				<Detail label="Entrypoint" value={text(input.entrypoint_path)} />
			</>
		);
	}
	return <Detail label="入力" value={JSON.stringify(input, null, 2)} />;
}

/** サーバーツールの実行前に表示する、承認待ち専用カード。 */
export function ToolApprovalPrompt() {
	const hitlState = useChatStore((s) => s.hitlState);
	const { submit, submitting, error } = useResolveToolApproval();
	const rootRef = useRef<HTMLDivElement>(null);
	const { setOptionRef, focusByOffset } = useChoiceOptionFocus();
	const active = hitlState?.kind === "tool-approval";

	useEffect(() => {
		if (active) rootRef.current?.focus();
	}, [active]);

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (event.target instanceof HTMLTextAreaElement) return;
			if (event.key === "ArrowDown") {
				event.preventDefault();
				focusByOffset(1);
				return;
			}
			if (event.key === "ArrowUp") {
				event.preventDefault();
				focusByOffset(-1);
			}
		},
		[focusByOffset],
	);

	if (!active) return null;

	const { toolUse, category, marker } = hitlState;
	const toolName = toolDisplayName(toolUse.name);
	const categoryLabel = CATEGORY_LABELS[category] ?? "その他";
	// ファイル編集はチャットのツールの行と同じく、作成か編集かまで出す。
	const editorAction =
		toolUse.name === "str_replace_based_edit_tool"
			? editorCommandLabel(toolUse.input.command)
			: null;
	const actionName = editorAction ?? toolName;
	const handleApproval = (decision: ToolApprovalDecision) => {
		void submit(decision).catch((caught: unknown) => {
			console.error("承認結果の送信に失敗しました", caught);
		});
	};

	return (
		<ChoiceCard
			testId="tool-approval-prompt"
			title="ツール実行の確認"
			// ファイルの読み込み・作成・編集は、それ自体が分類 (ファイル読み取り・編集) を表すので繰り返さない。
			context={
				editorAction !== null || categoryLabel === toolName
					? actionName
					: `${actionName}（${categoryLabel}）`
			}
			hint={
				error ? (
					<span style={{ color: "var(--danger)" }}>{error}</span>
				) : (
					"↑/↓ で移動 · Enter で決定"
				)
			}
			rootRef={rootRef}
			onKeyDown={handleKeyDown}
			busy={submitting}
			details={
				<>
					<ToolDetails
						name={toolUse.name}
						input={toolUse.input}
						workingDirectory={marker.workingDirectory}
					/>
					<Detail label="確認の理由" value={approvalReason(marker)} />
					{toolUse.name === "submit_model_training" && marker.manifest && (
						<>
							<Detail
								label="入力ファイル"
								value={`${marker.manifest.fileCount}件 / ${formatBytes(marker.manifest.totalBytes)}`}
							/>
							<Detail label="Manifest digest" value={marker.manifest.digest} />
						</>
					)}
				</>
			}
		>
			<div className="space-y-1.5">
				<ToolApprovalOptionButton
					index={0}
					label="許可"
					onClick={() => handleApproval("approve")}
					buttonRef={setOptionRef(0)}
				/>
				<ToolApprovalOptionButton
					index={1}
					label="拒否"
					onClick={() => handleApproval("deny")}
					buttonRef={setOptionRef(1)}
				/>
			</div>
		</ChoiceCard>
	);
}
